import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

const money = (value, currency = 'USD') => value == null ? 'Unknown' : new Intl.NumberFormat('en-IN', {
  style: 'currency', currency, minimumFractionDigits: currency === 'USD' ? 6 : 4, maximumFractionDigits: 6,
}).format(Number(value));
const count = (value) => value == null ? 'Unknown' : value.toLocaleString('en-IN');
const buttonClass = 'border border-zinc-300 px-3 py-2 text-sm hover:bg-zinc-50 disabled:opacity-50';

export default function AIUsagePanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/admin/ai-usage', { params: { limit: pageSize, offset } })
      .then(({ data }) => { if (active) { setItems(data.generations); setTotal(data.total); } })
      .catch(() => { if (active) setError('Unable to load usage records. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [offset, refresh]);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setDetailError('');
    if (selectedId) {
      api.get(`/admin/ai-usage/${selectedId}`)
        .then(({ data }) => { if (active) setDetail(data); })
        .catch(() => { if (active) setDetailError('Unable to load call details. Please refresh to retry.'); });
    }
    return () => { active = false; };
  }, [selectedId, refresh]);

  const download = useCallback(() => {
    if (!detail) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(detail, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-usage-${detail.id}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [detail]);

  return (
    <section className="mt-10 border-t border-zinc-200 pt-8" aria-labelledby="ai-usage-title">
      <div className="flex items-center justify-between gap-4">
        <h2 id="ai-usage-title" className="font-display text-2xl font-bold text-zinc-950">AI usage and report costs</h2>
        <button type="button" className={buttonClass} disabled={loading} onClick={() => setRefresh((value) => value + 1)}>Refresh usage</button>
      </div>
      <p className="mt-2 mb-4 max-w-4xl text-sm text-zinc-600">
        New report generations only. Costs are estimates from provider-reported usage and saved rates, before taxes and billing discounts.
        Goal suggestions and other calls made before Generate Report are excluded. Search costs are included separately.
        Unknown calls are excluded from the known subtotal, so a partial subtotal is not the full report cost.
      </p>
      {error && <p role="alert" className="mb-3 text-sm text-red-700">{error}</p>}
      {loading ? <p role="status" className="py-6 text-sm text-zinc-500">Loading usage...</p> : !error && (
        <>
          <div className="overflow-x-auto border border-zinc-200">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-600"><tr>
                {['Generated', 'Assessment / User', 'Outcome', 'Known tokens (in / out)', 'Calls / Fallbacks', 'Estimated cost', 'Details'].map((heading) => <th key={heading} className="px-4 py-3">{heading}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-zinc-200">
                {items.map((item) => <tr key={item.id}>
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(item.started_at).toLocaleString()}</td>
                  <td className="px-4 py-3">{item.assessment_id == null ? 'Not saved' : `#${item.assessment_id}`} / User {item.user_id}</td>
                  <td className="px-4 py-3">{item.status}{!item.tracking_complete && <div className="text-xs text-amber-800">Tracking incomplete</div>}</td>
                  <td className="px-4 py-3">{count(item.summary.input_tokens)} / {count(item.summary.output_tokens)}</td>
                  <td className="px-4 py-3">{item.summary.calls} / {item.summary.fallback_calls}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>{money(item.summary.known_subtotal_usd)}</div>
                    <div className={`text-xs ${item.summary.cost_complete ? 'text-zinc-500' : 'text-amber-800'}`}>
                      {item.summary.cost_complete ? 'Complete estimate' : `Partial · ${item.summary.unknown_cost_calls} unknown calls`}
                    </div>
                    {item.summary.total_estimated_inr != null && <div className="text-xs text-zinc-500">{money(item.summary.total_estimated_inr, 'INR')}</div>}
                  </td>
                  <td className="px-4 py-3"><button type="button" className="text-[#002FA7] underline" onClick={() => setSelectedId(item.id)}>View calls</button></td>
                </tr>)}
                {!items.length && <tr><td colSpan={7} className="px-4 py-8 text-zinc-500">No tracked generations yet. Generate a report to record its usage; previous reports cannot be backfilled accurately.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm text-zinc-600">
            <span>{total ? `${offset + 1}–${Math.min(offset + pageSize, total)} of ${total}` : '0 generations'}</span>
            <div className="flex gap-2">
              <button type="button" className={buttonClass} disabled={offset === 0} onClick={() => { setOffset(Math.max(0, offset - pageSize)); setSelectedId(null); }}>Previous</button>
              <button type="button" className={buttonClass} disabled={offset + pageSize >= total} onClick={() => { setOffset(offset + pageSize); setSelectedId(null); }}>Next</button>
            </div>
          </div>
        </>
      )}
      {selectedId && <div className="mt-6 border border-zinc-200 p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold">Generation details</h3>
          <button type="button" className={buttonClass} onClick={() => setSelectedId(null)}>Close details</button>
        </div>
        {detailError ? <p role="alert" className="mt-3 text-red-700">{detailError}</p> : !detail ? <p role="status" className="mt-3">Loading calls...</p> : <>
          <p className="mt-3 break-all text-xs text-zinc-500">Generation {detail.id}</p>
          <p className="my-3 text-sm">
            Known AI cost: {money(detail.summary.known_ai_subtotal_usd)} · Known search cost: {money(detail.summary.known_search_subtotal_usd)} ({detail.summary.search_credits} reported credits)
          </p>
          <p className="mb-3 text-xs text-zinc-600">INR is available when AI_USAGE_USD_INR is configured and all call costs are known. Degraded means a provider failed or the report used a fallback. Pending calls may indicate an interrupted request.</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px] text-left text-xs">
              <thead className="bg-zinc-50"><tr>{['Section', 'Provider / Model', 'Attempt', 'Status', 'Input', 'Cached input', 'Output', 'Search credits', 'Estimated USD'].map((heading) => <th key={heading} className="p-3">{heading}</th>)}</tr></thead>
              <tbody className="divide-y divide-zinc-200">{detail.calls.map((call) => <tr key={call.id}>
                <td className="p-3">{call.task}</td>
                <td className="p-3"><div>{call.provider}</div><div>Requested: {call.requested_model}</div><div>Returned: {call.returned_model || 'Not reported'}</div></td>
                <td className="p-3">{call.attempt}{call.fallback && <div>Fallback</div>}</td>
                <td className="p-3">{call.status}{call.error_type && <div>{call.error_type}{call.http_status ? ` (${call.http_status})` : ''}</div>}{call.error_category && <div>{call.error_category.replaceAll('_', ' ')}</div>}{call.provider_error_code && <div>{call.provider_error_code}</div>}</td>
                <td className="p-3">{call.provider === 'tavily' ? '—' : count(call.usage.input_tokens)}</td>
                <td className="p-3">{call.provider === 'tavily' ? '—' : count(call.usage.cached_input_tokens)}</td>
                <td className="p-3">{call.provider === 'tavily' ? '—' : count(call.usage.output_tokens)}</td>
                <td className="p-3">{call.provider === 'tavily' ? count(call.usage.search_credits) : '—'}</td>
                <td className="p-3">{money(call.cost_usd)}<div className="text-zinc-500">{call.pricing_status.replaceAll('_', ' ')}</div></td>
              </tr>)}</tbody>
            </table>
          </div>
          <button type="button" className={`${buttonClass} mt-4`} onClick={download}>Download usage JSON</button>
        </>}
      </div>}
    </section>
  );
}
